import { useEffect, useRef, useState } from 'react'
import {
  Combobox,
  ETypeOfNotification,
  InputColumnTableProps,
  InputSwitch,
  InputTable,
  InputTextCustom,
  Modal,
  setNotification,
} from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { useDispatch } from 'react-redux'
import LocationInlineEditActions from '../Location/components/LocationInlineEditActions'
import useResizeListener from '../../hooks/useResizeListener'
import LocationMobileActionMenu, { LocationMobileActionItem } from '../Location/tabs/components/LocationMobileActionMenu'
import { useMedicalSpecialitiesService } from '../../servicesHooks/MedicalSpecialities/useMedicalSpecialitiesService'
import {
  MedicalSpecialityItemDTO,
  MedicalSpecialityProfileDTO,
} from '../../dto/medicalspecialities/MedicalSpecialitiesDTO'
import './MedicalSpecialitiesView.scss'

interface FormState {
  medicalspeciality: number
  name: string
  entityTagId: number
  entityTagDesc: string
  allowsPrescription: boolean
  systemSpeciality: boolean
}

interface FormErrors {
  name?: string
  entityTagId?: string
}

const defaultFormState: FormState = {
  medicalspeciality: -1,
  name: '',
  entityTagId: -1,
  entityTagDesc: '',
  allowsPrescription: false,
  systemSpeciality: false,
}

export default function MedicalSpecialitiesView() {
  const dispatch = useDispatch()
  const {
    createMedicalSpeciality,
    deleteMedicalSpeciality,
    getMedicalSpecialityById,
    getMedicalSpecialities,
    getMedicalSpecialityProfiles,
    updateMedicalSpeciality,
  } = useMedicalSpecialitiesService()
  const isMobile = useResizeListener(768)
  const inputTableTopRef = useRef<HTMLDivElement | null>(null)
  const [focusNameInputToken, setFocusNameInputToken] = useState(0)
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<MedicalSpecialityItemDTO[]>([])
  const [profiles, setProfiles] = useState<MedicalSpecialityProfileDTO[]>([])
  const [selectedProfile, setSelectedProfile] = useState<MedicalSpecialityProfileDTO | null>(null)
  const [formState, setFormState] = useState<FormState>(defaultFormState)
  const [formErrors, setFormErrors] = useState<FormErrors>({})
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [itemToDelete, setItemToDelete] = useState<MedicalSpecialityItemDTO | null>(null)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [mobileModalVisible, setMobileModalVisible] = useState(false)
  const isEditing = editIndex !== null

  const readOnlySystemRow = isEditing && formState.systemSpeciality

  async function loadData() {
    setLoading(true)
    try {
      const [specialities, profileList] = await Promise.all([
        getMedicalSpecialities(),
        getMedicalSpecialityProfiles(),
      ])

      const sortedProfiles = [...profileList].sort((a, b) => a.name.localeCompare(b.name))
      setProfiles(sortedProfiles)
      setItems(specialities.sort((a, b) => a.name.localeCompare(b.name)))
    } catch (error) {
      console.error(error)
      dispatch(setNotification({
        title: 'Error',
        desc: 'No se pudieron cargar las especialidades médicas.',
        icon: 'pi pi-times',
        typeOfNotification: ETypeOfNotification.ERROR,
      }))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isEditing || selectedProfile || profiles.length === 0) return
    const byId = profiles.find((profile) => profile.id === formState.entityTagId) ?? null
    const byDesc = findProfileByDescription(formState.entityTagDesc) ?? null
    setSelectedProfile(byId ?? byDesc)
  }, [formState.entityTagDesc, formState.entityTagId, isEditing, profiles, selectedProfile])

  useEffect(() => {
    if (!isEditing || focusNameInputToken === 0) return

    const timer = window.setTimeout(() => {
      const nameInput = inputTableTopRef.current?.querySelector<HTMLInputElement>('.medical-specialities-name-input input')
      if (!nameInput) return

      nameInput.focus()
      const endPosition = nameInput.value?.length ?? 0
      nameInput.setSelectionRange(endPosition, endPosition)
    }, 0)

    return () => window.clearTimeout(timer)
  }, [focusNameInputToken, isEditing])

  function resetForm() {
    setEditIndex(null)
    setSelectedProfile(null)
    setFormErrors({})
    setFormState({
      ...defaultFormState,
    })
  }

  function normalizeLabel(value: string): string {
    return value
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
  }

  function findProfileByDescription(description: string): MedicalSpecialityProfileDTO | null {
    const normalizedDescription = normalizeLabel(description)
    if (normalizedDescription === '') return null
    return profiles.find((profile) => normalizeLabel(profile.name) === normalizedDescription) ?? null
  }

  function resolveProfileId(item: MedicalSpecialityItemDTO): number {
    if (item.entitytag > 0) return item.entitytag
    const matchedProfile = findProfileByDescription(item.entitytagdesc ?? '')
    return matchedProfile?.id ?? -1
  }

  function validateForm(): FormErrors {
    const nextErrors: FormErrors = {}
    if (!readOnlySystemRow && formState.name.trim() === '') {
      nextErrors.name = 'El nombre es obligatorio.'
    }
    if (!readOnlySystemRow && formState.entityTagId <= 0) {
      nextErrors.entityTagId = 'Seleccioná un perfil.'
    }
    return nextErrors
  }

  async function handleSave() {
    const validationErrors = validateForm()
    setFormErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) return false

    try {
      if (isEditing) {
        await updateMedicalSpeciality(formState.medicalspeciality, {
          name: formState.name.trim(),
          entityTagId: formState.entityTagId,
          allowsPrescription: formState.allowsPrescription,
        })
        dispatch(setNotification({
          title: 'Cambios guardados correctamente',
          icon: 'pi pi-check',
          typeOfNotification: ETypeOfNotification.INFO,
        }))
      } else {
        await createMedicalSpeciality({
          name: formState.name.trim(),
          entityTagId: formState.entityTagId,
          allowsPrescription: formState.allowsPrescription,
        })
        dispatch(setNotification({
          title: 'Creación exitosa',
          desc: 'La especialidad médica se creó correctamente.',
          icon: 'pi pi-check',
          typeOfNotification: ETypeOfNotification.INFO,
        }))
      }

      await loadData()
      resetForm()
      return true
    } catch (error: unknown) {
      const response = (error as any)?.response
      const status = typeof response?.status === 'number' ? response.status : 0
      if (status === 409) {
        dispatch(setNotification({
          title: 'No se pudo guardar',
          desc: 'Ya existe una especialidad médica con ese nombre.',
          icon: 'pi pi-exclamation-triangle',
          typeOfNotification: ETypeOfNotification.WARNING,
        }))
      } else {
        dispatch(setNotification({
          title: 'Error',
          desc: 'No se pudo guardar la especialidad médica.',
          icon: 'pi pi-times',
          typeOfNotification: ETypeOfNotification.ERROR,
        }))
      }
      return false
    }
  }

  async function startEdit(
    item: MedicalSpecialityItemDTO,
    index: number,
    options?: { scrollAndFocus?: boolean },
  ): Promise<boolean> {
    const shouldScrollAndFocus = options?.scrollAndFocus ?? true
    try {
      if (shouldScrollAndFocus) {
        inputTableTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }

      const detail = await getMedicalSpecialityById(item.medicalspeciality)
      setEditIndex(index)
      setFormErrors({})

      const resolvedProfileId = resolveProfileId(detail)
      const resolvedProfile = profiles.find((profile) => profile.id === resolvedProfileId)
        ?? findProfileByDescription(detail.entitytagdesc ?? '')
        ?? null

      setSelectedProfile(resolvedProfile)
      setFormState({
        medicalspeciality: detail.medicalspeciality,
        name: detail.name ?? '',
        entityTagId: resolvedProfile?.id ?? resolvedProfileId,
        entityTagDesc: detail.entitytagdesc ?? '',
        allowsPrescription: detail.allowsprescription,
        systemSpeciality: detail.systemspeciality,
      })
      if (shouldScrollAndFocus) {
        setFocusNameInputToken((current) => current + 1)
      }
      return true
    } catch (error) {
      console.error(error)
      dispatch(setNotification({
        title: 'Error',
        desc: 'No se pudo cargar el detalle de la especialidad médica para editar.',
        icon: 'pi pi-times',
        typeOfNotification: ETypeOfNotification.ERROR,
      }))
      return false
    }
  }

  function formatCardValue(value: string | null | undefined, emptyText = 'Sin datos') {
    const normalized = String(value ?? '').trim()
    return normalized !== '' ? normalized : emptyText
  }

  function openAddModal() {
    resetForm()
    setMobileModalVisible(true)
  }

  async function openEditModal(item: MedicalSpecialityItemDTO, index: number) {
    const ok = await startEdit(item, index, { scrollAndFocus: false })
    if (ok) {
      setMobileModalVisible(true)
    }
  }

  function closeMobileModal() {
    setMobileModalVisible(false)
    resetForm()
  }

  async function saveFromMobileModal() {
    const result = await handleSave()
    if (result) {
      setMobileModalVisible(false)
    }
  }

  function confirmDelete(item: MedicalSpecialityItemDTO) {
    setItemToDelete(item)
    setDeleteModalVisible(true)
  }

  async function handleDelete() {
    if (!itemToDelete) {
      setDeleteModalVisible(false)
      return
    }

    try {
      await deleteMedicalSpeciality(itemToDelete.medicalspeciality)
      dispatch(setNotification({
        title: 'Eliminación exitosa',
        desc: 'La especialidad médica se eliminó correctamente.',
        icon: 'pi pi-check',
        typeOfNotification: ETypeOfNotification.INFO,
      }))
      await loadData()
    } catch (error: unknown) {
      const response = (error as any)?.response
      const status = typeof response?.status === 'number' ? response.status : 0
      if (status === 409) {
        dispatch(setNotification({
          title: 'No se puede eliminar',
          desc: 'La especialidad médica está siendo utilizada en otras relaciones.',
          icon: 'pi pi-exclamation-triangle',
          typeOfNotification: ETypeOfNotification.WARNING,
        }))
      } else {
        dispatch(setNotification({
          title: 'Error',
          desc: 'No se pudo eliminar la especialidad médica.',
          icon: 'pi pi-times',
          typeOfNotification: ETypeOfNotification.ERROR,
        }))
      }
    } finally {
      setDeleteModalVisible(false)
      setItemToDelete(null)
    }
  }

  const columns: InputColumnTableProps<MedicalSpecialityItemDTO>[] = [
    {
      header: 'Nombre',
      className: 'medical-specialities-col medical-specialities-col--name',
      style: { paddingLeft:'8px'},
      body: (item) => item.name,
      input: (
        <InputTextCustom
          className={`medical-specialities-name-input w-full ${formErrors.name ? '' : 'border-grey'}`}
          value={formState.name}
          disable={readOnlySystemRow}
          onChange={(event) => {
            setFormState((current) => ({ ...current, name: event.target.value }))
            setFormErrors((current) => ({ ...current, name: undefined }))
          }}
          error={Boolean(formErrors.name)}
          caption={formErrors.name}
        />
      ),
    },
    {
      header: 'Perfil',
      className: 'medical-specialities-col medical-specialities-col--profile',
      body: (item) => item.entitytagdesc || '',
      input: (
        <div className='medical-specialities-profile-input'>
          <Combobox
            className={`w-full ${formErrors.entityTagId ? '' : 'border-grey'}`}
            getItems={() => Promise.resolve(profiles)}
            value={selectedProfile}
            setValue={(event: MedicalSpecialityProfileDTO | null) => {
              setSelectedProfile(event)
              setFormState((current) => ({
                ...current,
                entityTagId: event?.id ?? -1,
                entityTagDesc: event?.name ?? '',
              }))
              setFormErrors((current) => ({ ...current, entityTagId: undefined }))
            }}
            optionLabel='name'
            error={Boolean(formErrors.entityTagId)}
            caption={formErrors.entityTagId}
            disable={readOnlySystemRow}
          />
        </div>
      ),
    },
    {
      header: 'Permite recetas',
      className: 'medical-specialities-col medical-specialities-col--allows',
      body: (item) => (
        <span className='medical-specialities-allows-value'>
          {item.allowsprescription ? 'Sí' : 'No'}
        </span>
      ),
      input: (
        <div className='medical-specialities-switch location-switch-inline-row location-desktop-inputtable-switch-row pb-2'>
          <InputSwitch
            id='medicalSpecialitiesAllowsPrescriptionInputSwitchId'
            className='w-full'
            checked={formState.allowsPrescription}
            onChange={(event) =>
              setFormState((current) => ({ ...current, allowsPrescription: Boolean(event.value) }))
            }
           
          />
          <span className='medical-specialities-switch-state'>{formState.allowsPrescription ? 'Sí' : 'No'}</span>
        </div>
      ),
    },
    {
      header: '',
      className: 'medical-specialities-col medical-specialities-col--actions',
      input: isEditing ? (
        <LocationInlineEditActions
          visible
          onConfirm={() => {
            void handleSave()
          }}
          onCancel={resetForm}
        />
      ) : (
        <Button
          type='button'
          icon='pi pi-plus-circle text-3xl'
          className='buttonSecondary2 p-button-rounded medical-specialities-add-btn'
          onClick={() => {
            void handleSave()
          }}
          aria-label='Crear especialidad médica'
        />
      ),
      body: (item, index) => (
        item.systemspeciality ? null : (
          <div className='medical-specialities-actions-body'>
            <Button
              type='button'
              icon='pi pi-file-edit'
              className='p-button-text medical-specialities-action-icon-btn'
              onClick={() => {
                void startEdit(item, index)
              }}
              aria-label='Editar especialidad médica'
            />
            <Button
              type='button'
              icon='pi pi-trash'
              className='p-button-text medical-specialities-action-icon-btn'
              onClick={() => confirmDelete(item)}
              aria-label='Eliminar especialidad médica'
            />
          </div>
        )
      ),
    },
  ]

  if (isMobile) {
    return (
      <div className='viewContainer medical-specialities-view medical-specialities-mobile-view'>
        <div className='medical-specialities-header'>
          <h1 className='medical-specialities-title'>Especialidades médicas</h1>
          <div className='medical-specialities-subtitle'>
            <i className='pi pi-info-circle medical-specialities-subtitle-icon' />
            <span>Solo podrán editarse las especialidades creadas a mano.</span>
          </div>
        </div>
        <div className='medical-specialities-divider' />

        <div className='medical-specialities-mobile-toolbar'>
          <Button
            className='medical-specialities-mobile-add-btn'
            icon='pi pi-plus-circle'
            label='Agregar'
            onClick={openAddModal}
          />
        </div>

        <div className='medical-specialities-mobile-card-list'>
          {(items ?? []).map((item, index) => {
            const menuItems: LocationMobileActionItem[] = [
              {
                label: 'Editar',
                icon: 'pi pi-file-edit',
                disabled: item.systemspeciality,
                action: () => { void openEditModal(item, index) },
              },
              {
                label: 'Eliminar',
                icon: 'pi pi-trash',
                disabled: item.systemspeciality,
                action: () => confirmDelete(item),
              },
            ]

            return (
              <article key={`medical-speciality-${item.medicalspeciality}-${index}`} className='medical-specialities-mobile-card'>
                <div className='medical-specialities-mobile-card-header'>
                  <h4 className='medical-specialities-mobile-card-title'>{formatCardValue(item.name, 'Sin nombre')}</h4>
                  <LocationMobileActionMenu items={menuItems} ariaLabel='Opciones de la especialidad médica' />
                </div>
                <div className='medical-specialities-mobile-card-row'>
                  <span className='medical-specialities-mobile-card-key'>Perfil</span>
                  <span className='medical-specialities-mobile-card-value'>{formatCardValue(item.entitytagdesc)}</span>
                </div>
                <div className='medical-specialities-mobile-card-row'>
                  <span className='medical-specialities-mobile-card-key'>Permite recetas</span>
                  <span className='medical-specialities-mobile-card-value'>{item.allowsprescription ? 'Sí' : 'No'}</span>
                </div>
                <div className='medical-specialities-mobile-card-row'>
                  <span className='medical-specialities-mobile-card-key'>Tipo</span>
                  <span className='medical-specialities-mobile-card-value'>{item.systemspeciality ? 'Especialidad de sistema' : 'Especialidad manual'}</span>
                </div>
              </article>
            )
          })}
          {(items ?? []).length === 0 && (
            <p className='medical-specialities-mobile-empty'>No hay especialidades médicas cargadas.</p>
          )}
        </div>

        <Modal
          className='medical-specialities-mobile-editor-modal'
          visible={mobileModalVisible}
          setVisible={setMobileModalVisible}
          width='w-medium'
          header={<h1 className='info-dark text-2xl'>{isEditing ? 'Editar especialidad médica' : 'Agregar especialidad médica'}</h1>}
          footerButtonLeftText='Cancelar'
          onClickLeftBtn={closeMobileModal}
          footerButtonRightText={isEditing ? 'Guardar' : 'Agregar'}
          onClickRightBtn={() => { void saveFromMobileModal() }}
          onHideCustom={closeMobileModal}
        >
          <div className='medical-specialities-mobile-modal-content'>
            <div>
              <label className='block mb-2'>Nombre <span className='text-red-500'>*</span></label>
              <InputTextCustom
                className={`medical-specialities-name-input w-full ${formErrors.name ? '' : 'border-grey'}`}
                value={formState.name}
                disable={readOnlySystemRow}
                onChange={(event) => {
                  setFormState((current) => ({ ...current, name: event.target.value }))
                  setFormErrors((current) => ({ ...current, name: undefined }))
                }}
                error={Boolean(formErrors.name)}
                caption={formErrors.name}
              />
            </div>
            <div>
              <label className='block mb-2'>Perfil <span className='text-red-500'>*</span></label>
              <Combobox
                className={`w-full ${formErrors.entityTagId ? '' : 'border-grey'}`}
                getItems={() => Promise.resolve(profiles)}
                value={selectedProfile}
                setValue={(event: MedicalSpecialityProfileDTO | null) => {
                  setSelectedProfile(event)
                  setFormState((current) => ({
                    ...current,
                    entityTagId: event?.id ?? -1,
                    entityTagDesc: event?.name ?? '',
                  }))
                  setFormErrors((current) => ({ ...current, entityTagId: undefined }))
                }}
                optionLabel='name'
                error={Boolean(formErrors.entityTagId)}
                caption={formErrors.entityTagId}
                disable={readOnlySystemRow}
              />
            </div>
            <div>
              <label className='block mb-2'>Permite recetas</label>
            </div>
            <div className='medical-specialities-mobile-switch-row location-switch-inline-row' style={{display: 'inline-flex', alignItems: 'center', gap: '8px'}}>
              <InputSwitch
                id='medicalSpecialitiesAllowsPrescriptionMobileInputSwitchId'
                checked={formState.allowsPrescription}
                onChange={(event) =>
                  setFormState((current) => ({ ...current, allowsPrescription: Boolean(event.value) }))
                }
              />
              <span className='medical-specialities-switch-state'>{formState.allowsPrescription ? 'Sí' : 'No'}</span>
            </div>
          </div>
        </Modal>

        <Modal
          visible={deleteModalVisible}
          setVisible={setDeleteModalVisible}
          width='w-medium'
          header={<p className='info-dark text-2xl'>Eliminar especialidad médica</p>}
          footerButtonLeftText='Cancelar'
          footerButtonRightText='Sí, eliminar'
          onClickLeftBtn={() => {
            setDeleteModalVisible(false)
            setItemToDelete(null)
          }}
          onClickRightBtn={() => {
            void handleDelete()
          }}
          onHideCustom={() => {
            setDeleteModalVisible(false)
            setItemToDelete(null)
          }}
        >
          <p className='medical-specialities-delete-message'>
            ¿Querés eliminar la especialidad médica{' '}
            <strong>{itemToDelete?.name ?? ''}</strong>?
          </p>
        </Modal>
      </div>
    )
  }

  return (
    <div className='viewContainer medical-specialities-view'>
      <div className='medical-specialities-header'>
        <h1 className='medical-specialities-title'>Especialidades médicas</h1>
        <div className='medical-specialities-subtitle'>
          <i className='pi pi-info-circle medical-specialities-subtitle-icon' />
          <span>Solo podrán editarse las especialidades creadas a mano.</span>
        </div>
      </div>
      <div className='medical-specialities-divider' />

      {!loading && (
        <div ref={inputTableTopRef}>
          <InputTable
            className='medical-specialities-inputtable'
            columnGap={0}
            headerDivClassName='font-semibold bg-bluegray-50'
            dividerClassName='my-1 border-divider'
            items={items}
            columns={columns}
            onAdd={() => {}}
            divBtnClassName='medical-specialities-hide-native-control'
            divider
            dividerInFirstItem
          />
        </div>
      )}

      <Modal
        visible={deleteModalVisible}
        setVisible={setDeleteModalVisible}
        width='w-medium'
        header={<p className='info-dark text-2xl'>Eliminar especialidad médica</p>}
        footerButtonLeftText='Cancelar'
        footerButtonRightText='Sí, eliminar'
        onClickLeftBtn={() => {
          setDeleteModalVisible(false)
          setItemToDelete(null)
        }}
        onClickRightBtn={() => {
          void handleDelete()
        }}
        onHideCustom={() => {
          setDeleteModalVisible(false)
          setItemToDelete(null)
        }}
      >
        <p className='medical-specialities-delete-message'>
          ¿Querés eliminar la especialidad médica{' '}
          <strong>{itemToDelete?.name ?? ''}</strong>?
        </p>
      </Modal>
    </div>
  )
}
